import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useTour} from "../../../providers/tour";

import {gql} from "@apollo/client";
import {isEditorIndexTourMock} from "../../../scenes/editor/index.tour";
import {Project, Challenge, ChallengeInput} from "../../../schema";

import {useModal} from "../../ModalProvider";
import MoveChallengeModal from "./MoveChallengeModal";
import EditItemDropdown from "../../EditItemDropdown";

type props = Pick<React.ComponentProps<typeof MoveChallengeModal.Content>, "projects" | "challenge"> & {
  challenge: Pick<Challenge, "id" | "name">,
  currentProject?: Pick<Project, "id">,
  disabled?: boolean,
  onCopy: (challengeId: Challenge["id"]) => void,
  onDelete: (challengeId: Challenge["id"]) => void,
  onMove: (challengeId: string, data: Pick<ChallengeInput, "projectId">) => void,
}

export default function ChallengeEditDropdown(
  {challenge, projects, currentProject, disabled, onCopy, onDelete, onMove}: props
) {
  const {t} = useTranslation();

  const {mock: tourMock} = useTour();

  const mock = useMemo(() => {
    if (tourMock && isEditorIndexTourMock(tourMock)) {
      return tourMock.getBy.challenge.get(challenge.id);
    }
  }, [challenge, tourMock]);
  const onDropdownClick = mock?.eventHandlers.dropdownClick;

  const currentProjectId = currentProject?.id;

  const onRefClick = useCallback(() => {
    onDropdownClick && setTimeout(onDropdownClick);
  }, [onDropdownClick]);

  const copyHandler = useCallback(() => {
    onCopy && onCopy(challenge.id);
  }, [onCopy, challenge.id]);

  const deleteHandler = useCallback(() => {
    onDelete && onDelete(challenge.id);
  }, [onDelete, challenge.id]);

  const {add: addModal} = useModal();

  const onMoveChallengeClick = useCallback(() => {
    const moveProject = (data: Pick<ChallengeInput, "projectId">) => {
      onMove(challenge.id, data)
      modal.remove()
    }

    const modal = addModal({
      header: <MoveChallengeModal.Header/>,
      content: (
        <MoveChallengeModal.Content
          challenge={challenge}
          initialId={currentProjectId}
          projects={projects}
          onMove={moveProject}
        />
      )
    })
  }, [challenge, currentProjectId, projects, onMove, addModal]);

  return (
    <EditItemDropdown
      className={mock?.classes.dropdownContent}
      buttonClassName={mock?.classes.dropdown}
      onRefClick={onRefClick}
    >
      <EditItemDropdown.Action title={t("common.copy")} onClick={copyHandler} disabled={disabled}/>
      <EditItemDropdown.Action
        title={t("components.ChallengeEditDropdown.move")}
        onClick={onMoveChallengeClick}
        disabled={disabled}/>
      <EditItemDropdown.Action title={t("common.delete")} onClick={deleteHandler} disabled={disabled}/>
    </EditItemDropdown>
  );
}

ChallengeEditDropdown.fragments = {
  root: gql`
    fragment ChallengeEditDropdown on Challenge {
      id
      name
      ...MoveChallengeModal
    }
    ${MoveChallengeModal.fragments.root}
  `,
  projects: gql`
    fragment ChallengeEditDropdownProjects on Project {
      ...MoveChallengeModalProjects
    }
    ${MoveChallengeModal.fragments.projects}
  `
}
